<template>
  <div qaid="dropShipOrdersContainer">
    <!-- MiniCart Header -->
    <div v-if="isMiniCart" class="flex justify-between">
      <heading id="miniCart__callout--empty" class="mb-3" :level="1" size="sm" qaid="dropShipOrdersContainer--header">
        {{ bp('yourContainers') }}
        <span class="text-md text-secondary ml-1">{{ fullCountText }}</span>
      </heading>
      <div v-if="volumeHTML" class="text-md text-secondary mb-3" v-html="volumeHTML" />
    </div>

    <!-- Cart Header -->
    <div v-else class="section-header section-header--dividing section-header--xl">
      <div class="flex items-end">
        <h1 class="heading heading--xl" qaid="dropShipOrdersContainer--header">
          {{ bp('yourContainers') }}
        </h1>
        <span class="heading--xl text-md text-secondary ml-3">{{ fullCountText }}</span>
        <span v-if="volumeHTML" class="flex content-end heading--xl text-md text-secondary ml-2" v-html="volumeHTML" />
        <i v-tippy="warningTippyConfig" class="material-icons md-18 text-black ml-25 pt-px cursor-pointer">info</i>
      </div>

      <!-- Calculation Warning tooltip -->
      <div id="warning-tooltip" v-tippy-html qaid="warningTooltip" class="text-left">
        {{ bp('containerDimension') }}
      </div>
    </div>

    <!-- Containers -->
    <template v-if="isMiniCart">
      <!-- Mini Cart Paginated containers -->
      <div class="flex flex-wrap" qaid="dropShipOrdersContainer--containers">
        <ContainerIndicator
          v-for="(indicator, index) in paginatedData"
          :key="index"
          class="mr-1 mb-2"
          :container="indicator.container"
          :drop-ship-container="dropShipContainer"
          :total-containers-amount="totalContainersAmount"
          :is-add-button="indicator.isAddButton"
          :is-limit-warning="indicator.isLimitWarning"
          :base-properties="baseProperties"
          :show-delete-button="true"
          qaid="dropShipOrdersContainer--containerIndicator"
          :is-mini-cart="true"
          @add-container="$emit('add-container')"
        />
      </div>

      <div v-if="pageCount > 1" class="flex justify-center mb-3">
        <pagination
          ref="pagination"
          :page-number="pageNumber"
          :page-count="pageCount"
          @page-change="pageChange"
          @prev-page="prevPage"
          @next-page="nextPage"
        />
      </div>
    </template>
    <template v-else>
      <!-- Cart non-paginated containers -->
      <div class="flex flex-wrap mb-3" qaid="dropShipOrdersContainer--containers">
        <ContainerIndicator
          v-for="(indicator, index) in listData"
          :key="`${index}-${indicator.container ? indicator.container.type : ''}`"
          class="mr-2 mb-2"
          :container="indicator.container"
          :drop-ship-container="dropShipContainer"
          :total-containers-amount="totalContainersAmount"
          :is-add-button="indicator.isAddButton"
          :is-limit-warning="indicator.isLimitWarning"
          :base-properties="baseProperties"
          :show-tooltip-config="true"
          :show-delete-button="!isCheckout"
          qaid="dropShipOrdersContainer--containerIndicator"
        />
      </div>
    </template>

    <p v-if="isCheckout && !isPrint">
      {{ bp('containerAdjustMessage') }}
      <a :href="`${encodedContextPath}/cart`">{{ bp('containerAdjustCTA') }}</a>
    </p>

    <div class="mt-3">
      <pdl-callout v-if="!isMiniCart && dropShipContainer && dropShipContainer.vendorNotificationText" kind="warning">
        <template #content>
          <span v-html="dropShipContainer.vendorNotificationText" />
        </template>
      </pdl-callout>
    </div>
  </div>
</template>

<script>
import pagination from '../../../mixins/pagination';
import {mapState} from 'vuex';
import Heading from '@/components/Heading';
import ContainerIndicator from '@/components/containers/drop-ship/ContainerIndicator';
import Pagination from '@/components/Pagination';
import {PdlCallout} from '@pedal/pdl-callout';
import reduce from 'lodash/reduce';

export default {
  components: {
    Heading,
    ContainerIndicator,
    Pagination,
    PdlCallout,
  },

  mixins: [pagination],

  props: {
    baseProperties: {
      type: Array,
      required: false,
      default: () => [],
    },
    dropShipContainer: {
      type: Object,
      required: false,
      default: () => null,
    },
    isMiniCart: Boolean,
    isCheckout: Boolean,
    isPrint: Boolean,
    size: {
      type: Number,
      required: false,
      default: 4,
    },
  },

  data() {
    return {
      maxContainers: 15,
      warningTippyConfig: {
        html: '#warning-tooltip',
        theme: 'light',
        trigger: 'mouseenter',
        maxWidth: '20rem',
        reactive: true,
      },
    };
  },

  computed: {
    listData() {
      let result = [];

      if (this.dropShipContainer) {
        this.dropShipContainer.containers?.forEach((container) => {
          let containersQuantity = 0;
          while (containersQuantity < container.totals.amount) {
            let ownConsumedVolume =
              Math.floor(container.totals.consumedVolume / container.volume) > containersQuantity
                ? container.volume
                : container.totals.consumedVolume - container.volume * containersQuantity;
            result.push({
              container: {...container, ownConsumedVolume: ownConsumedVolume > 0 ? ownConsumedVolume : 0},
              isAddButton: false,
              isLimitWarning: false,
            });
            containersQuantity++;
          }
        });

        if (!this.isCheckout && this.totalContainersAmount < this.maxContainers) {
          result.push({container: null, isAddButton: true, isLimitWarning: false});
        } else if (!this.isCheckout && this.totalContainersAmount >= this.maxContainers) {
          result.push({container: null, isAddButton: false, isLimitWarning: true});
        }
      }

      return result;
    },

    totalFullContainersAmount() {
      return reduce(
        this.dropShipContainer.containers,
        (totalAmount, container) =>
          container.volume > 0
            ? totalAmount + Math.floor(container.totals.consumedVolume / container.volume)
            : totalAmount,
        0
      );
    },

    fullCountText() {
      if (this.dropShipContainer) {
        return this.bp('containerFullCount')
          .replace('{0}', this.totalFullContainersAmount)
          .replace('{1}', this.totalContainersAmount);
      }

      return null;
    },

    volumeHTML() {
      if (this.dropShipContainer?.consumedVolume && this.dropShipContainer?.totalVolume) {
        return this.bp('containerVolumeCount')
          .replace('{0}', parseFloat(Math.ceil(this.dropShipContainer.consumedVolume * 100) / 100).toFixed(2))
          .replace('{1}', parseFloat(Math.floor(this.dropShipContainer.totalVolume * 100) / 100).toFixed(2));
      }

      return null;
    },

    totalContainersAmount() {
      return (
        this.dropShipContainer.containers.reduce(
          (totalAmount, container) => (totalAmount += container.totals.amount),
          0
        ) || null
      );
    },

    ...mapState('backend', ['encodedContextPath']),
  },
};
</script>
